import { EntityTitle } from '@app/icare/components/entity/EntityTitle'
import { PatientApplicantEditDrawer } from '@app/icare/entities/patient/edits/PatientApplicantEditDrawer'
import { patientApplicantQueries } from '@app/icare/entities/patient/queries/patientApplicantQueries'
import {
  patientFindingsQueryOptions,
  selectByGroupAndGroupByRelationId,
} from '@app/icare/entities/patient/queries/patientFindingsQueryOptions'
import {
  type PatientApplicantRelation,
  PatientApplicantTypeEnum,
} from '@app/icare/entities/patient/schema/PatientApplicant'
import { PatientId } from '@app/icare/entities/patient/schema/PatientId'
import { RelationOverview } from '@app/icare/routes/_app/patienten/$id/_edit/-components/RelationOverview'
import { isActivePatientRelation } from '@app/icare/shared/api'
import { t } from '@lingui/core/macro'
import { entityParams } from '@pkg/db/utils'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useRelationCommitNavigation } from '../-components/useRelationCommitNavigation'

export const Route = createFileRoute('/_app/patienten/$id/_edit/_applicant/anmelder/')({
  params: entityParams({ id: PatientId }),
  loader: ({ params, context: { queryClient } }) =>
    queryClient.ensureQueryData(patientApplicantQueries.forIndex(params.id)),
  component: RouteComponent,
})

function RouteComponent() {
  const { id } = Route.useParams()
  const navigate = Route.useNavigate()
  const { data: applicants = [] } = useSuspenseQuery(patientApplicantQueries.forIndex(id))
  const { data: findings = {} } = useQuery({
    ...patientFindingsQueryOptions(id),
    select: selectByGroupAndGroupByRelationId('applicants'),
  })

  // Lingui macros must run inside a component so the active locale is bound at render-time,
  // not at module load. (Doctor/Clinic dodge this by reading from a defineEntityConfig factory.)
  const labels = { label: t`Anmelder`, labelPlural: t`Anmelder` }

  const handleAfterCommit = useRelationCommitNavigation<PatientApplicantRelation>({
    isInactive: relation => !isActivePatientRelation(relation),
    onCreateNavigate: relation =>
      navigate({
        to: '/patienten/$id/anmelder/$relation',
        params: { id, relation: relation.id },
      }),
    onInactiveEditNavigate: () => navigate({ to: '.', replace: true }),
  })
  return (
    <RelationOverview
      items={applicants}
      labels={labels}
      findings={findings}
      basePath={`/patienten/${id}/anmelder`}
      getLabel={item => {
        if (item.type === PatientApplicantTypeEnum.PATIENT_OR_RELATIVE) {
          return t`Patient oder Angehöriger`
        }

        if (item.toId) {
          return <EntityTitle id={item.toId} />
        }

        return t`Unbekannt`
      }}
      onEdit={params =>
        void PatientApplicantEditDrawer.show({ patientId: id, onAfterCommit: handleAfterCommit, ...params })
      }
    />
  )
}
